/**
 * Shared axis arithmetic for the Recharts leaves: round tick steps, evenly
 * spaced year ticks and locale tick labels with thousands separators.
 */

/** 1, 2 or 5 times a power of ten: the steps a reader counts in. */
export function niceStep(rawStep: number): number {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const residual = rawStep / magnitude;
  if (residual <= 1) return magnitude;
  if (residual <= 2) return 2 * magnitude;
  if (residual <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

/**
 * Ticks on one round step inside [min, max], at most `maxTicks` of them.
 * Integer steps only: an axis of years or classes never prints 2027.5.
 */
export function evenTicks(min: number, max: number, maxTicks: number): number[] {
  const step = Math.max(1, niceStep((max - min) / Math.max(1, maxTicks - 1)));
  const ticks: number[] = [];
  for (let tick = Math.ceil(min / step) * step; tick <= max; tick += step) ticks.push(tick);
  return ticks;
}

/** The page language Recharts tick labels are formatted in. */
function chartLocale(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return document.documentElement.lang || undefined;
}

/**
 * Value-axis tick label: the page locale's grouping (6 000, 6,000) and at
 * most two decimals, so a 1 000+ axis never prints a bare "6000".
 */
export function axisTickFormatter(value: number): string {
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat(chartLocale(), {
    maximumFractionDigits: 2,
  }).format(value);
}
